import { useMemo, useState } from "react";
import { useParams } from "react-router-dom";
import PageMeta from "@/components/feature/PageMeta";
import Breadcrumb from "@/components/feature/Breadcrumb";
import MediaGrid from "@/components/feature/MediaGrid";
import Pagination from "@/components/feature/Pagination";
import EmptyState from "@/components/feature/EmptyState";
import FilterSidebar from "@/pages/category/components/FilterSidebar";
import {
  getMediaByType,
  filterMedia,
  sortByNewest,
  sortByPopular,
  paginate,
  totalPages,
  categoryLabel,
  PAGE_SIZE,
  type MediaFilters,
} from "@/lib/media";

const EMPTY_FILTERS: MediaFilters = { regions: [], years: [], genres: [] };

type SortKey = "newest" | "popular";

export default function Category() {
  const { type } = useParams();
  const isValid = type === "movie" || type === "tv";
  const label = categoryLabel(type);

  const [filters, setFilters] = useState<MediaFilters>(EMPTY_FILTERS);
  const [sort, setSort] = useState<SortKey>("newest");
  const [page, setPage] = useState(1);

  const baseList = useMemo(() => getMediaByType(type), [type]);

  const filtered = useMemo(() => {
    const result = filterMedia(baseList, filters);
    return sort === "newest" ? sortByNewest(result) : sortByPopular(result);
  }, [baseList, filters, sort]);

  const total = totalPages(filtered.length, PAGE_SIZE);
  const current = Math.min(page, total);
  const paged = paginate(filtered, current, PAGE_SIZE);

  const toggle = <K extends keyof MediaFilters>(group: K, value: MediaFilters[K][number]) => {
    setFilters((prev) => {
      const list = prev[group] as (typeof value)[];
      const nextList = list.includes(value)
        ? list.filter((v) => v !== value)
        : [...list, value];
      return { ...prev, [group]: nextList } as MediaFilters;
    });
    setPage(1);
  };

  const resetFilters = () => {
    setFilters(EMPTY_FILTERS);
    setPage(1);
  };

  const title = isValid ? `蜜桃成熟 - ${type === "tv" ? "电视剧列表" : "电影大全"}` : "蜜桃成熟 - 分类";
  const description = isValid
    ? `蜜桃成熟${label}分类列表，支持按地区、年份、类型多维度筛选，并可按最新上线或热门排序。`
    : "蜜桃成熟影视分类列表。";

  return (
    <>
      <PageMeta title={title} description={description} />

      <div className="w-full px-4 md:px-8 py-6 md:py-8">
        <Breadcrumb
          items={[
            { label: "首页", to: "/" },
            { label: "分类" },
            { label },
          ]}
        />

        <div className="mt-5 mb-8">
          <h1 className="text-2xl md:text-3xl font-heading font-semibold text-foreground-950">
            蜜桃成熟 · {type === "tv" ? "电视剧列表" : "电影大全"}
          </h1>
          <p className="text-sm text-foreground-500 mt-2">
            共收录 {baseList.length} 部{label}，可按地区、年份与类型叠加筛选。
          </p>
        </div>

        {!isValid ? (
          <EmptyState
            icon="ri-error-warning-line"
            title="分类不存在"
            description="没有找到对应的分类，请从下方进入电影或电视剧列表。"
          />
        ) : (
          <div className="flex flex-col lg:flex-row gap-6 lg:gap-8 items-start">
            <FilterSidebar
              filters={filters}
              onToggleRegion={(v) => toggle("regions", v)}
              onToggleYear={(v) => toggle("years", v)}
              onToggleGenre={(v) => toggle("genres", v)}
              onReset={resetFilters}
            />

            <div className="flex-1 min-w-0 w-full">
              <div className="flex items-center justify-between gap-3 mb-5 pb-4 border-b border-background-200">
                <p className="text-sm text-foreground-600">
                  筛选结果 <span className="text-primary-600 font-medium">{filtered.length}</span> 部
                </p>
                <div className="flex items-center gap-1 p-1 rounded-full border border-background-200 bg-background-50">
                  {(["newest", "popular"] as SortKey[]).map((key) => (
                    <button
                      key={key}
                      type="button"
                      onClick={() => {
                        setSort(key);
                        setPage(1);
                      }}
                      className={`px-4 py-1.5 rounded-full text-xs md:text-sm whitespace-nowrap transition-colors cursor-pointer ${
                        sort === key
                          ? "bg-primary-500 text-background-50"
                          : "text-foreground-600 hover:text-primary-600"
                      }`}
                    >
                      {key === "newest" ? "最新上线" : "热门排序"}
                    </button>
                  ))}
                </div>
              </div>

              {paged.length === 0 ? (
                <EmptyState
                  icon="ri-search-eye-line"
                  title="没有符合条件的作品"
                  description="试试减少筛选条件，或一键重置后再看看。"
                  actionLabel="重置筛选条件"
                  onAction={resetFilters}
                />
              ) : (
                <>
                  <MediaGrid items={paged} showSynopsis />
                  <div className="mt-10">
                    <Pagination page={current} totalPages={total} onChange={setPage} />
                  </div>
                </>
              )}
            </div>
          </div>
        )}
      </div>
    </>
  );
}